import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, of, type Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LoginService } from '@/app/core/api/api/login.service';
import { PasskeysService } from '@/app/core/api/api/passkeys.service';
import type { EmailConfirm200Response } from '@/app/core/api/model/emailConfirm200Response';
import type { MerchantLoginResult } from '@core/api/model/merchantLoginResult';
import { MerchantLoginResultAnyOf } from '@core/api/model/merchantLoginResultAnyOf';
import type { PasskeyAuthenticationVerifyRequest } from '@core/api/model/passkeyAuthenticationVerifyRequest';
import type { PasskeyRegistrationVerifyResponse } from '@core/api/model/passkeyRegistrationVerifyResponse';
import { PasskeyLoginError } from '@/app/core/errors/passkey-login.error';
import { getTokenExpirationMs } from '@shared/utils/jwt-utils';
import { AuthStorageService } from './auth-storage.service';
import { CurrentUserService } from './current-user.service';
import { InitialDataLoaderService } from './initial-data-loader.service';
import { LayoutStoreService } from './layout-store.service';
import { WebAuthnService } from './webauthn/webauthn.service';
import type { PersistedMerchantUser } from '@core/api/model/persistedMerchantUser';
import { MerchantAuthInit } from '@core/api/model/merchantAuthInit';
import { getDefaultAuthenticatedRoute } from '@shared/utils/default-authenticated-route';
import { ignorePromiseRejection } from '@shared/utils/promise-utils';

const SIGN_IN_ROUTE = '/auth/sign-in';
const PASSKEY_SETUP_ROUTE = '/auth/passkey-setup';

/**
 * Runtime union of `phoneConfirm` next steps. The generated flattened
 * `MerchantLoginResult` only types `AUTHENTICATED` (anyOf generator quirk),
 * but the API can also return `PASSKEY_SETUP_REQUIRED` with a `setupToken`.
 */
type MerchantLoginNextStep =
  | MerchantLoginResult.NextStepEnum
  | MerchantLoginResultAnyOf.NextStepEnum;

/**
 * Merchant portal authentication service — 3-step email/SMS token flow.
 *
 * Step 1: initLogin(email)        → POST /login/init       → interim session token
 * Step 2: confirmEmail(token, otp) → POST /login/emailConfirm → masked phone number
 * Step 3: confirmPhone(token, otp, trusted) → POST /login/phoneConfirm → final JWT
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly loginService = inject(LoginService);
  private readonly passkeysService = inject(PasskeysService);
  private readonly webAuthnService = inject(WebAuthnService);
  private readonly authStorage = inject(AuthStorageService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly initialDataLoaderService = inject(InitialDataLoaderService);
  private readonly layoutStoreService = inject(LayoutStoreService);
  private readonly router = inject(Router);

  readonly isAuthenticated = this.currentUserService.isAuthenticated;
  readonly currentUser = this.currentUserService.currentUser;

  /**
   * Step 1 — Initiate login by sending email.
   * Returns the interim session token (merchantLoginToken).
   */
  initLogin(email: string): Observable<string> {
    return this.loginService.auth({ email, country: MerchantAuthInit.CountryEnum.Pl }).pipe(
      map((res) => {
        const token = res.merchantLoginToken;
        if (token == null || token === '')
          throw new Error('No session token received from login init');
        return token;
      }),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * Step 2 — Confirm email OTP.
   * Returns masked phone number for display.
   */
  confirmEmail(sessionToken: string, otp: string): Observable<EmailConfirm200Response> {
    return this.loginService
      .emailConfirm({ token: sessionToken, secret: otp })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * Step 3 — Confirm SMS OTP and complete login.
   *
   * When the user already has a passkey (or setup is not enforced) the API
   * returns `nextStep: AUTHENTICATED` with the final JWT — the user is logged
   * in and navigated to the app. When the user has no passkey yet, the API
   * returns `nextStep: PASSKEY_SETUP_REQUIRED` with a short-lived `setupToken`
   * and the user is redirected to the forced passkey setup page.
   */
  confirmPhone(sessionToken: string, otp: string, trustedBrowser: boolean): Observable<void> {
    this.initialDataLoaderService.startTracking();
    this.layoutStoreService.setIsLoading(true);

    return this.loginService
      .phoneConfirm({ token: sessionToken, secret: otp, trustedBrowser })
      .pipe(
        switchMap((res) => this.handleLoginResult(res)),
        catchError((err) => {
          this.layoutStoreService.setIsLoading(false);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Sign in with a WebAuthn passkey (replaces the email/SMS flow).
   *
   * On failure, the error may be a {@link PasskeyLoginError} whose `stage`
   * identifies the failing step: `init` (POST …/passkeys/authentication/init),
   * `webauthn` (browser credential prompt), `verify` (POST …/authentication/verify),
   * `token` (missing JWT in verify response), `whoami` (loading the user profile).
   */
  loginWithPasskey(): Observable<void> {
    if (!this.webAuthnService.isPasskeySupported()) {
      return throwError(() => new Error('Passkeys are not supported in this browser.'));
    }

    this.initialDataLoaderService.startTracking();
    this.layoutStoreService.setIsLoading(true);

    return this.passkeysService.authenticationInit().pipe(
      catchError((error: unknown) => throwError(() => new PasskeyLoginError('init', error))),
      switchMap((init) =>
        from(this.webAuthnService.createAuthenticationResponse(init.options)).pipe(
          map(
            (response): PasskeyAuthenticationVerifyRequest => ({
              sessionId: init.sessionId,
              response,
            }),
          ),
          catchError((error: unknown) =>
            throwError(() => new PasskeyLoginError('webauthn', error)),
          ),
        ),
      ),
      switchMap((body) =>
        this.passkeysService
          .authenticationVerify(body)
          .pipe(
            catchError((error: unknown) =>
              throwError(() => new PasskeyLoginError('verify', error)),
            ),
          ),
      ),
      switchMap((res) => {
        const token = res.merchantLoginToken;
        if (token == null || token === '') {
          return throwError(
            () => new PasskeyLoginError('token', new Error('No token received from server')),
          );
        }
        return this.completeLoginFlow(token).pipe(
          catchError((error: unknown) => throwError(() => new PasskeyLoginError('whoami', error))),
        );
      }),
      catchError((err) => {
        this.layoutStoreService.setIsLoading(false);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Registers a passkey during the forced first-login setup.
   * Both API calls are authenticated with the setup token
   * (`merchantPanelPasskeySetup` credential wired in app.config.ts).
   *
   * @param name - Display name for the new passkey
   * @returns Verify response carrying the final session JWT
   */
  registerPasskeyLoginSetup(name: string): Observable<PasskeyRegistrationVerifyResponse> {
    // Setup-token endpoints (merchantPanelPasskeySetup), not the whoami variants.
    return this.passkeysService.registrationInit_1().pipe(
      switchMap((init) =>
        from(this.webAuthnService.createRegistrationResponse(init.options)).pipe(
          switchMap((response) =>
            this.passkeysService.registrationVerify_2({
              sessionId: init.sessionId,
              name,
              response,
            }),
          ),
        ),
      ),
    );
  }

  /**
   * Persists the JWT returned by passkey registration verify and loads the
   * current user without navigation (the setup page shows its own success UI).
   *
   * @param token - Session JWT returned by POST …/passkeys/registration/verify
   */
  completeSetupWithToken(token: string): Observable<void> {
    if (token == null || token === '') {
      return throwError(() => new Error('No token received from server'));
    }
    this.authStorage.removePasskeySetupToken();
    this.saveTokenWithExpiration(token);
    return this.loadCurrentUser().pipe(map(() => undefined));
  }

  /**
   * Cancels the forced passkey setup flow (e.g. the setup token expired or
   * the user's browser cannot complete WebAuthn) and returns to sign-in so
   * the user can restart the login flow and get a fresh setup token.
   */
  abandonPasskeySetup(): void {
    this.authStorage.removePasskeySetupToken();
    ignorePromiseRejection(this.router.navigate([SIGN_IN_ROUTE]));
  }

  loadCurrentUser(): Observable<PersistedMerchantUser> {
    return this.loginService.whoami().pipe(
      tap((user) => {
        this.currentUserService.setCurrentUser(user);
      }),
      catchError((err) => {
        this.logout();
        return throwError(() => err);
      }),
    );
  }

  logout(): void {
    this.initialDataLoaderService.stopTracking();
    this.layoutStoreService.setIsLoading(false);
    this.authStorage.removeToken();
    this.authStorage.removeTokenExpiration();
    this.authStorage.removePasskeySetupToken();
    this.currentUserService.clearCurrentUser();
    ignorePromiseRejection(this.router.navigate([SIGN_IN_ROUTE]));
  }

  refreshToken(): Observable<string> {
    return this.loginService.refresh().pipe(
      map((res) => {
        const token = res.merchantLoginToken;
        if (token == null || token === '') throw new Error('No token received from refresh');
        this.saveTokenWithExpiration(token);
        return token;
      }),
      catchError((err) => {
        this.logout();
        return throwError(() => err);
      }),
    );
  }

  isUserAuthenticated(): boolean {
    return this.authStorage.hasToken();
  }

  initializeSession(): Observable<PersistedMerchantUser | null> {
    if (!this.authStorage.hasToken()) {
      return throwError(() => new Error('No token found'));
    }

    this.initialDataLoaderService.startTracking();
    this.layoutStoreService.setIsLoading(true);

    return this.loadCurrentUser().pipe(
      catchError(() => {
        this.logout();
        return throwError(() => new Error('Failed to initialize session'));
      }),
    );
  }

  /**
   * Branches on the `phoneConfirm` result: either redirects to the forced
   * passkey setup page (storing the setup token) or completes the login.
   */
  private handleLoginResult(res: MerchantLoginResult): Observable<void> {
    const nextStep = res.nextStep as MerchantLoginNextStep;

    if (nextStep === MerchantLoginResultAnyOf.NextStepEnum.PasskeySetupRequired) {
      const setupToken: string | undefined = res.setupToken;
      if (setupToken == null || setupToken === '') {
        throw new Error('Passkey setup required but no setup token received');
      }
      this.authStorage.savePasskeySetupToken(setupToken);
      this.initialDataLoaderService.stopTracking();
      this.layoutStoreService.setIsLoading(false);
      ignorePromiseRejection(this.router.navigate([PASSKEY_SETUP_ROUTE]));
      return of(undefined);
    }

    const jwt: string | undefined = res.merchantLoginToken;
    if (jwt == null || jwt === '') throw new Error('No JWT received from phone confirm');
    return this.completeLoginFlow(jwt);
  }

  /**
   * Saves the session JWT, loads the current user and navigates to the
   * default authenticated route.
   */
  private completeLoginFlow(token: string): Observable<void> {
    this.authStorage.removePasskeySetupToken();
    this.saveTokenWithExpiration(token);
    return this.loadCurrentUser().pipe(
      tap((user) => {
        ignorePromiseRejection(
          this.router.navigate([getDefaultAuthenticatedRoute(user.merchantUserPrivileges)]),
        );
      }),
      map(() => undefined),
    );
  }

  private saveTokenWithExpiration(token: string): void {
    this.authStorage.saveToken(token);
    const expirationMs = getTokenExpirationMs(token);
    if (expirationMs !== null) {
      this.authStorage.saveTokenExpiration(expirationMs);
    }
  }
}
